/**
 * Formát souboru pro export a import rozložení.
 *
 * Od chvíle, kdy je zdrojem pravdy databáze, je tenhle soubor **jediný způsob, jak dostat
 * rozložení ven** — na zálohu, na přenos na jinou instanci, nebo aby ho někdo vložil do nové
 * seed migrace. Nahrazuje to, co dřív obstarával `defaults.ts` v gitu.
 *
 * ⚠ **V souboru ZÁMĚRNĚ není `version`.** Kdyby tam byla a import ji použil, dostaneš buď
 * věčné 409 (soubor je starý), nebo — mnohem hůř — při náhodné shodě čísel tiše přepíšeš
 * práci někoho jiného. Import proto bere `doc` a `name` ze souboru, ale **živou verzi ze
 * storu**. `key` v souboru slouží jen ke kontrole, že člověk neimportuje dashboard do PIPu.
 *
 * Čistý modul bez importů z Reactu ani axiosu, aby šel testovat sám za sebe.
 */

import { validateLayout, type LayoutDoc } from "@globals/layouts";

export const LAYOUT_FILE_FORMAT = "demirel-layout";
/** Verze OBALU souboru, ne dokumentu uvnitř (ten má vlastní `schemaVersion`). */
export const LAYOUT_FILE_VERSION = 1;

export interface LayoutFile {
  format: typeof LAYOUT_FILE_FORMAT;
  formatVersion: number;
  key: string;
  name: string;
  /** Jen informativní — ať je z otevřeného souboru poznat, kdy vznikl. */
  exportedAt: string;
  doc: LayoutDoc;
}

export const buildLayoutFile = (key: string, name: string, doc: LayoutDoc, now: Date): LayoutFile => ({
  format: LAYOUT_FILE_FORMAT,
  formatVersion: LAYOUT_FILE_VERSION,
  key,
  name,
  exportedAt: now.toISOString(),
  doc,
});

/**
 * `demirel-layout-dashboard-20260808T2140Z.json`
 *
 * UTC a bez dvojteček — jméno tak jde použít na Windows a **lexikografické řazení odpovídá
 * chronologickému** (stejný postoj jako u záloh PicoW, `picow/paths.ts:24-25`).
 */
export const layoutFileName = (key: string, now: Date): string => {
  const t = now.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z").replace(/(\d{8}T\d{4})\d{2}Z/, "$1Z");
  return `demirel-layout-${key.replace(/:/g, "-")}-${t}.json`;
};

export type ParseResult = { ok: true; file: LayoutFile } | { ok: false; error: string };

export const parseLayoutFile = (text: string): ParseResult => {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: "Soubor není platný JSON." };
  }

  if (typeof raw !== "object" || raw === null) return { ok: false, error: "Soubor neobsahuje objekt." };
  const o = raw as Partial<LayoutFile>;

  if (o.format !== LAYOUT_FILE_FORMAT) {
    return { ok: false, error: "Tohle není exportované rozložení Demirelu." };
  }
  if (o.formatVersion !== LAYOUT_FILE_VERSION) {
    return { ok: false, error: `Neznámá verze formátu (${String(o.formatVersion)}).` };
  }
  if (typeof o.key !== "string" || o.key.length === 0) return { ok: false, error: "V souboru chybí klíč plochy." };
  if (typeof o.name !== "string" || o.name.length === 0) return { ok: false, error: "V souboru chybí název." };

  // Dokument projde stejnou validací jako všechno ostatní — starší export může porušovat
  // pravidlo, které přibylo později, a nemá projít jen proto, že se tváří jako náš soubor.
  const v = validateLayout(o.doc);
  if (!v.ok) return { ok: false, error: `Rozložení v souboru je neplatné: ${v.errors[0] ?? "?"}` };

  return {
    ok: true,
    file: {
      format: LAYOUT_FILE_FORMAT,
      formatVersion: LAYOUT_FILE_VERSION,
      key: o.key,
      name: o.name,
      exportedAt: typeof o.exportedAt === "string" ? o.exportedAt : "",
      doc: v.doc,
    },
  };
};
